/**
 * useProjectProgressSummary — backward-compatible wrapper around useProjectSummary.
 *
 * All components that previously used useProjectProgressSummary continue to work.
 * Internally delegates to useProjectSummary which is the single source of truth.
 *
 * The old implementation used useStageScheduleChecklist which had a 30-second
 * module-level cache and multiple separate calculation paths. This wrapper
 * eliminates that by using the direct Supabase query approach.
 */

import { useMemo } from 'react';
import { useProjectSummary } from './useProjectSummary';
import { useStageScheduleChecklist } from './useStageScheduleChecklist';
import { computeChecklistProgress } from '@/lib/checklistProgress';

export interface StageSummary {
  name: string;
  status: string;
  scheduleProgress: {
    totalItems: number;
    completedItems: number;
    unfinishedItems: number;
    progressPercentage: number;
    totalProgress: number;
  };
  checklistCompletionPct: number;
  checklistCount: number;
  completedChecklistCount: number;
  accentColor: string;
  bgColor: string;
  icon: string;
  combinedProgress: number;
  scheduleBinaryPct: number;
  checklistFinalPct: number;
}

export interface ProjectProgressSummary {
  // Checklist counts
  managementCompleted: number;
  managementTotal: number;
  subcontractorCompleted: number;
  subcontractorTotal: number;
  checklistFinalCompleted: number;
  checklistFinalTotal: number;
  checklistFinalProgress: number;

  // Schedule counts (FINAL — unmapped items count as complete)
  scheduleFinalCompleted: number;
  scheduleFinalTotal: number;
  scheduleFinalProgress: number;

  // Display schedule counts (for UI bars — uses live checklist-derived values)
  scheduleDisplayCompleted: number;
  scheduleDisplayTotal: number;
  scheduleDisplayProgress: number;

  // Combined
  projectProgress: number;

  // Stage
  activeStage: string;
  stagesSummary: StageSummary[];

  // Flags
  loaded: boolean;
  loading: boolean;
  subWaiting: boolean;
  mgmtPct: number;
  subPct: number;
}

/**
 * Single source of truth for all project progress numbers.
 *
 * Uses useProjectSummary internally for accurate, fresh data.
 * Also uses useStageScheduleChecklist for stage-level breakdown (stagesSummary).
 */
export function useProjectProgressSummary(
  projectId: string | undefined,
  options?: { stagesEnabled?: boolean }
): ProjectProgressSummary {
  // PRIMARY: use the direct Supabase query hook for accurate numbers
  const summary = useProjectSummary(projectId);

  // SECONDARY: per-stage breakdown — only when explicitly enabled (e.g. Stages tab)
  const ssc = useStageScheduleChecklist(projectId || '', {
    enabled: options?.stagesEnabled ?? false,
  });

  const stagesSummary: StageSummary[] = useMemo(
    () =>
      ssc.stageSummary.map((s) => ({
        name: s.name,
        status: s.status,
        scheduleProgress: s.scheduleProgress,
        checklistCompletionPct: s.checklistCompletionPct,
        checklistCount: s.checklistCount,
        completedChecklistCount: s.completedChecklistCount,
        accentColor: s.accentColor,
        bgColor: s.bgColor,
        icon: s.icon,
        combinedProgress: s.combinedProgress,
        scheduleBinaryPct: s.scheduleBinaryPct,
        checklistFinalPct: s.checklistFinalPct,
      })),
    [ssc.stageSummary]
  );

  // Debug comparison log — header vs schedule tab
  // eslint-disable-next-line no-console
  if (summary.loaded) {
    // eslint-disable-next-line no-console
    console.log('Header project summary', {
      scheduleFinalProgress: summary.scheduleFinalProgress,
      checklistFinalProgress: summary.checklistFinalProgress,
      projectProgress: summary.projectProgress,
      source: 'header',
    });
  }

  return {
    // Checklist — from useProjectSummary (accurate, fresh)
    managementCompleted: summary.managementCompleted,
    managementTotal: summary.managementTotal,
    subcontractorCompleted: summary.subcontractorCompleted,
    subcontractorTotal: summary.subcontractorTotal,
    checklistFinalCompleted: summary.checklistFinalCompleted,
    checklistFinalTotal: summary.checklistFinalTotal,
    checklistFinalProgress: summary.checklistFinalProgress,

    // Schedule (FINAL — for project progress calculation) — from useProjectSummary
    scheduleFinalCompleted: summary.scheduleFinalCompleted,
    scheduleFinalTotal: summary.scheduleFinalTotal,
    scheduleFinalProgress: summary.scheduleFinalProgress,

    // Schedule (DISPLAY — for UI bars) — from ssc for backward compat
    scheduleDisplayCompleted: ssc.stages.reduce((s, st) => s + st.scheduleProgress.completedItems, 0),
    scheduleDisplayTotal: ssc.stages.reduce((s, st) => s + st.scheduleProgress.totalItems, 0),
    scheduleDisplayProgress: ssc.overallScheduleProgress,

    // Combined — from useProjectSummary (accurate, fresh)
    projectProgress: summary.projectProgress,

    // Stage
    activeStage: summary.activeStage,
    stagesSummary,

    // Flags — from useProjectSummary
    loaded: summary.loaded,
    loading: summary.loading,
    subWaiting: summary.subWaiting,
    mgmtPct: summary.mgmtPct,
    subPct: summary.subPct,
  };
}